import type { TermLabel } from "../../../api/content/ontologyApi";
import type { VitaAssembly } from "../../../api/threads/batchesApi";
import type { Extraction } from "../../../api/knowledgeData/knowledgeDataDecoders";
import type { InstanceStatement } from "../../../api/knowledgeData/knowledgeDataDecoders";
import type { InstanceDataSet } from "../../../api/threads/liveMessages";
import { readableTriple } from "../../../shared/formatting/triples";
import { parseNTriple, RDF_TYPE } from "../../../shared/rdf/nTriples";
import { orderedLabels } from "../../ontology/viewer/ontologyModel";

/** Predicates that name an individual: its name, not a row. */
const NAME_PREDICATES: ReadonlySet<string> = new Set([
  "http://www.w3.org/2000/01/rdf-schema#label",
  "http://www.w3.org/2004/02/skos/core#prefLabel",
]);

export interface Individual {
  /** `<iri>`, as N-Triples writes it. */
  readonly iri: string;
  /** The label the data gives it, in the reader's language where it has one; `null` without. */
  readonly name: string | null;
  /** The words of the first statement that types it, which a page may name it by without a label. */
  readonly words: string | null;
  /** The IRIs of the classes it is typed with. */
  readonly classes: string[];
  readonly lines: { triple: string; text: string; statement: InstanceStatement }[];
}

/**
 * The individuals the statements describe, by subject, with their names and classes (F94): the
 * one grouping of instance data, for the result's view and a document's regest. Keep the
 * server's claim numbers: one statement may describe several individuals.
 */
export function groupIndividuals(statements: readonly InstanceStatement[], language: string): Individual[] {
  const groups = new Map<string, Individual & { classes: string[]; lines: Individual["lines"][number][]; labels: TermLabel[]; words: string | null }>();
  // Its name is a label the data gives it; its words, those of the first statement that types it.
  for (const statement of statements) {
    for (const triple of statement.triples) {
      const parsed = parseNTriple(triple);
      if (parsed?.subject.kind !== "iri") continue;
      const iri = `<${parsed.subject.iri}>`;
      let group = groups.get(iri);
      if (group === undefined) {
        group = { iri, name: null, words: null, classes: [], lines: [], labels: [] };
        groups.set(iri, group);
      }
      const { predicate, object } = parsed;
      if (predicate === RDF_TYPE && object.kind === "iri" && !group.classes.includes(object.iri)) group.classes.push(object.iri);
      if (NAME_PREDICATES.has(predicate) && object.kind === "literal") group.labels.push(object);
      if (predicate === RDF_TYPE && group.words === null && statement.evidence.length > 0) group.words = statement.evidence.map((span) => span.quote).join(" … ");
      group.lines.push({ triple, text: readableTriple(triple), statement });
    }
  }
  const firstOffset = (group: Individual) => Math.min(...group.lines.flatMap(({ statement }) => statement.evidence.map((span) => span.start)));
  return [...groups.values()]
    .map(({ iri, classes, lines, labels, words }) => ({ iri, name: orderedLabels(labels, language)[0]?.value ?? null, words, classes, lines }))
    .sort((a, b) => firstOffset(a) - firstOffset(b));
}

/** A value a statement gives a property: an individual the data describes, a model term, or text. */
export type StatementValue =
  | { readonly kind: "individual"; readonly individual: Individual }
  | { readonly kind: "term"; readonly iri: string }
  | { readonly kind: "text"; readonly text: string };

/** What one statement says about one individual: its properties, each with its values. */
export interface StatementDescription {
  readonly individual: Individual;
  readonly rows: readonly { readonly property: string; readonly values: readonly StatementValue[] }[];
}

/**
 * What a statement says, individual by individual, as Daniel's regest viewer shows it (F94):
 * the properties other than its classes and names; an object the data describes as that
 * individual, a term of the data model (`isTerm`, the term owner's answer) as the term, and
 * any other IRI as an individual the data only names. `individuals` is `groupIndividuals`' answer.
 */
export function describeStatement(statement: InstanceStatement, individuals: readonly Individual[], isTerm: (iri: string) => boolean): StatementDescription[] {
  const byIri = new Map(individuals.map((individual) => [individual.iri, individual]));
  const descriptions = new Map<string, Map<string, StatementValue[]>>();
  for (const triple of statement.triples) {
    const parsed = parseNTriple(triple);
    if (parsed?.subject.kind !== "iri") continue;
    const subject = `<${parsed.subject.iri}>`;
    const rows = descriptions.get(subject) ?? new Map<string, StatementValue[]>();
    descriptions.set(subject, rows);
    const { predicate, object } = parsed;
    if (predicate === RDF_TYPE || NAME_PREDICATES.has(predicate)) continue;
    let value: StatementValue;
    if (object.kind !== "iri") {
      value = { kind: "text", text: object.kind === "literal" ? object.text : object.label };
    } else {
      const described = byIri.get(`<${object.iri}>`);
      value = described !== undefined ? { kind: "individual", individual: described }
        : isTerm(object.iri) ? { kind: "term", iri: object.iri }
        : { kind: "individual", individual: { iri: `<${object.iri}>`, name: null, words: null, classes: [], lines: [] } };
    }
    rows.set(predicate, [...(rows.get(predicate) ?? []), value]);
  }
  return [...descriptions].flatMap(([iri, rows]) => {
    const individual = byIri.get(iri);
    return individual === undefined ? [] : [{ individual, rows: [...rows].map(([property, values]) => ({ property, values })) }];
  });
}

/** A statement to show on its own, and the statements it shows for: those that only name an individual it gives as a value. */
export interface ShownStatement<T> {
  readonly shown: T;
  readonly absorbed: readonly T[];
}

/**
 * Statements shown together, without repeating one (O-1): a statement that only types and names
 * individuals another of them gives as values (a Place "Sonstwo" that a Petition's "benefice in"
 * names) is shown by that value, with its class, and not again on its own, unless it has a note.
 * The first statement that names it takes it; order is kept.
 */
export function absorbNamedIndividuals<T extends { readonly statement: InstanceStatement }>(
  items: readonly T[],
  individuals: readonly Individual[],
  isTerm: (iri: string) => boolean,
): ShownStatement<T>[] {
  const described = items.map((item) => ({ item, descriptions: describeStatement(item.statement, individuals, isTerm) }));
  const namedOnly = (descriptions: readonly StatementDescription[]) => descriptions.length > 0 && descriptions.every((description) => description.rows.length === 0);
  const valuesOf = (descriptions: readonly StatementDescription[]) => new Set(descriptions.flatMap((description) => description.rows.flatMap((row) => row.values.flatMap((value) => (value.kind === "individual" ? [value.individual.iri] : [])))));
  const absorber = new Map<T, T>();
  for (const { item, descriptions } of described) {
    // One with a note keeps its own line, where the note shows.
    if (!namedOnly(descriptions) || item.statement.note !== "") continue;
    const named = descriptions.map((description) => description.individual.iri);
    const by = described.find((other) => other.item !== item && !namedOnly(other.descriptions) && named.every((iri) => valuesOf(other.descriptions).has(iri)));
    if (by !== undefined) absorber.set(item, by.item);
  }
  return described
    .filter(({ item }) => !absorber.has(item))
    .map(({ item }) => ({ shown: item, absorbed: items.filter((other) => absorber.get(other) === item) }));
}

/** Its classes without those above another of them (`Petition`, not also `Event`), by the data model's parents. */
export function mostSpecificClasses(classes: readonly string[], parentsOf: (iri: string) => readonly string[]): string[] {
  const general = new Set<string>();
  const todo = classes.flatMap(parentsOf);
  for (let next = todo.pop(); next !== undefined; next = todo.pop()) {
    if (general.has(next)) continue;
    general.add(next);
    todo.push(...parentsOf(next));
  }
  return classes.filter((iri) => !general.has(iri));
}

export function wholeGraph(statements: readonly InstanceStatement[]): string {
  return [...new Set(statements.flatMap((statement) => statement.triples))].join("\n");
}

/** Statements not recorded as one result: a proposal under review, or a vita assembled from its parts' results. */
export interface UnrecordedInstanceData {
  /** What its statements are keyed by and asked about as: the Thread entry that holds them, where there is one. */
  readonly source: string;
  /** Where selecting their words asks the agent about them (F96); `undefined`: not asked about here. */
  readonly projectId: string | undefined;
  /** The document version (`<document>@<version>`) whose text they are drawn on. */
  readonly document: string;
  /** The model version they were written against (`<collection>@<commit>`), where their terms open. */
  readonly model: string;
  readonly accepted: boolean;
  readonly instanceStatements: readonly InstanceStatement[];
}

/**
 * A vita assembled from its parts' results as the instance-data view draws it: on the vita's
 * text, its terms opening at the first part's model version, accepted once every part is.
 */
export function assembledVita(vita: string, parts: readonly Pick<Extraction, "model" | "state">[], statements: readonly InstanceStatement[]): UnrecordedInstanceData {
  return {
    source: vita,
    projectId: undefined,
    document: vita,
    model: parts[0]?.model ?? "",
    accepted: parts.length > 0 && parts.every((part) => part.state === "accepted"),
    instanceStatements: statements,
  };
}

/**
 * A step's proposed set as statements the instance-data view draws (F154): numbered in its
 * order from 1, as the text an agent reads numbers them; without IRIs, which recording gives.
 */
export function proposedStatements(set: InstanceDataSet): InstanceStatement[] {
  return set.statements.map((statement, index) => ({
    number: index + 1,
    iri: "",
    note: statement.note,
    triples: statement.triples,
    evidence: statement.evidence.map((span) => ({ ...span, segmentIri: "" })),
  }));
}

/** Match assemble_vita: plan-order statements, sorted triple keys, document evidence only. */
export function assembleResults(manifest: VitaAssembly, results: readonly Pick<Extraction, "source" | "instanceStatements">[]) {
  const groups = new Map<string, { statements: InstanceStatement[]; parts: Set<string> }>();
  for (const output of manifest.partOutputs) {
    const result = results.find((result) => result.source === output);
    if (result === undefined) throw new Error(`Missing part output: ${output}`);
    for (const statement of result.instanceStatements) {
      if (!statement.evidence.some((span) => span.segment.split("#")[0] === manifest.vita)) continue;
      const key = JSON.stringify([...statement.triples].sort());
      const group = groups.get(key) ?? { statements: [], parts: new Set<string>() };
      group.statements.push(statement);
      group.parts.add(output);
      groups.set(key, group);
    }
  }
  const membership = [...groups.values()].map((group) => [...group.parts].sort());
  if (JSON.stringify(membership) !== JSON.stringify(manifest.membership)) throw new Error("Assembly membership differs from its part results.");
  const statements = [...groups.values()].map((group, index): InstanceStatement => {
    const first = group.statements[0];
    if (first === undefined) throw new Error("Empty assembly statement.");
    const evidence = new Map(group.statements.flatMap((statement) => statement.evidence).map((span) => [JSON.stringify([span.segment, span.start, span.end]), span]));
    return { ...first, number: index + 1, triples: [...first.triples].sort(), evidence: [...evidence.values()], note: [...new Set(group.statements.map((statement) => statement.note).filter(Boolean))].join(" / ") };
  });
  return { statements, membership, complete: manifest.complete, missingUnits: manifest.missingUnits };
}
